import { usePermissions } from '@/hooks/usePermissions'

export interface PermissionGateProps {
  module: string
  children: React.ReactNode
  fallback?: React.ReactNode
}

export interface PermissionGateSingleActionProps extends PermissionGateProps {
  action: string
  actions?: never
}

export interface PermissionGateAnyActionProps extends PermissionGateProps {
  action?: never
  actions: string[]
}

export type PermissionGateRequiredProps =
  | PermissionGateSingleActionProps
  | PermissionGateAnyActionProps

/**
 * Renders children only when the user has the required permission (from GET /users/me roleId.permissions).
 * Use for action buttons (create, update, delete, export). When loading or without permission, renders fallback or null.
 */
export function PermissionGate({
  module,
  action,
  actions,
  children,
  fallback = null,
}: PermissionGateRequiredProps): React.ReactNode {
  const { can, canAny, isLoading } = usePermissions()

  if (isLoading) return fallback

  const allowed = actions
    ? canAny(module, actions)
    : action
      ? can(module, action)
      : false

  return allowed ? children : fallback
}

PermissionGate.displayName = 'PermissionGate'
